import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { fetchContent } from "@/lib/api";
import { FadeUp, MaskedLines, RevealImage } from "@/components/Reveal";
import Lightbox from "@/components/Lightbox";
import { KanthaStitch } from "@/components/Motifs";

export default function CollectionDetail() {
  const { slug } = useParams();
  const [col, setCol] = useState(null);
  const [next, setNext] = useState(null);
  const [error, setError] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 180]);

  useEffect(() => {
    setCol(null);
    setError(false);
    fetchContent(`/collections/${slug}`)
      .then((data) => (data && data.slug ? setCol(data) : setError(true)))
      .catch(() => setError(true));
    fetchContent("/collections?all=true").then((list) => {
      const idx = list.findIndex((c) => c.slug === slug);
      if (idx >= 0) setNext(list[(idx + 1) % list.length]);
    });
  }, [slug]);

  if (error) {
    return (
      <main className="pt-40 pb-40 text-center" data-testid="collection-not-found">
        <p className="font-serif text-4xl text-ink">This chapter has not been written yet.</p>
        <Link to="/collections" className="link-underline inline-block mt-6 text-[11px] tracking-[0.25em] uppercase text-alta">Back to the archive</Link>
      </main>
    );
  }

  if (!col) {
    return <main className="pt-40 pb-40 text-center text-ink/50 font-light" data-testid="collection-loading">Unfolding the collection…</main>;
  }

  return (
    <main data-testid="collection-detail-page">
      {/* immersive hero */}
      <section className="relative hero-svh-88 overflow-hidden bg-ink">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <img src={col.hero_image} alt={col.title} className="w-full h-[115%] object-cover hero-breathe" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-ink/10" />
        <div className="absolute inset-0 flex flex-col justify-end max-w-[1500px] mx-auto px-5 md:px-10 pb-14 md:pb-20">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-paper/75 text-[11px] tracking-[0.35em] uppercase mb-5"
          >
            Collection — {col.year}
          </motion.p>
          <MaskedLines
            lines={[col.title]}
            delay={0.5}
            className="font-serif font-light text-paper text-6xl sm:text-7xl lg:text-9xl tracking-wide"
          />
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9 }}
            className="mt-6 max-w-xl font-serif italic text-paper/85 text-lg md:text-2xl font-light"
          >
            {col.concept}
          </motion.p>
        </div>
      </section>

      {/* concept statement */}
      <section className="py-24 md:py-36">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <FadeUp>
            <p className="text-[11px] tracking-[0.3em] uppercase text-alta mb-8">The Concept</p>
            <p className="font-serif font-light text-2xl sm:text-3xl lg:text-4xl leading-[1.4] text-walnut">
              {col.description}
            </p>
          </FadeUp>

          <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-10">
            {col.mood && (
              <FadeUp delay={0.05}>
                <p className="text-[10px] tracking-[0.3em] uppercase text-terracotta mb-4">Mood</p>
                <p className="text-sm font-light text-ink/70 leading-relaxed">{col.mood}</p>
              </FadeUp>
            )}
            {col.materials?.length > 0 && (
              <FadeUp delay={0.1}>
                <p className="text-[10px] tracking-[0.3em] uppercase text-terracotta mb-4">Materials</p>
                <ul className="space-y-2">
                  {col.materials.map((m) => (
                    <li key={m} className="text-sm font-light text-ink/70">{m}</li>
                  ))}
                </ul>
              </FadeUp>
            )}
            {col.techniques?.length > 0 && (
              <FadeUp delay={0.15}>
                <p className="text-[10px] tracking-[0.3em] uppercase text-terracotta mb-4">Craft Techniques</p>
                <ul className="space-y-2">
                  {col.techniques.map((t) => (
                    <li key={t} className="text-sm font-light text-ink/70">{t}</li>
                  ))}
                </ul>
              </FadeUp>
            )}
          </div>

          {col.cultural_references && (
            <FadeUp className="mt-16 bg-linen kantha-border p-8 md:p-10">
              <p className="text-[10px] tracking-[0.3em] uppercase text-maroon mb-4">Cultural References</p>
              <p className="font-serif italic text-xl md:text-2xl font-light text-walnut">{col.cultural_references}</p>
            </FadeUp>
          )}
        </div>
      </section>

      {/* lookbook */}
      {col.images?.length > 0 && (
        <section className="py-16 md:py-24 bg-bone" data-testid="lookbook-section">
          <div className="max-w-[1500px] mx-auto px-5 md:px-10">
            <FadeUp className="mb-14 flex items-baseline justify-between">
              <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink">The Lookbook</h2>
              <span className="font-bengali text-terracotta/60 text-xl">রূপকথা</span>
            </FadeUp>
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
              {col.images.map((src, i) => (
                <FadeUp key={i} delay={(i % 3) * 0.08} className="mb-6 break-inside-avoid">
                  <RevealImage
                    src={src}
                    alt={`${col.title} look ${i + 1}`}
                    className={i % 3 === 0 ? "aspect-[3/4]" : i % 3 === 1 ? "aspect-[4/5]" : "aspect-square"}
                    cursor="VIEW"
                    onClick={() => setLightbox({ image: src, title: `${col.title} — Look ${String(i + 1).padStart(2, "0")}`, category: "Lookbook", medium: col.year })}
                  />
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* next collection */}
      {next && next.slug !== col.slug && (
        <Link to={`/collections/${next.slug}`} data-testid="next-collection-link" data-cursor="EXPLORE" className="group block relative py-28 md:py-40 bg-ink text-paper text-center overflow-hidden grain">
          <p className="text-[11px] tracking-[0.35em] uppercase text-paper/50 mb-5">Next Chapter</p>
          <h2 className="font-serif font-light text-5xl sm:text-7xl lg:text-8xl tracking-wide group-hover:text-alta transition-colors duration-700">
            {next.title}
          </h2>
          <p className="mt-5 text-sm font-light text-paper/60">{next.concept}</p>
        </Link>
      )}

      <Lightbox item={lightbox} onClose={() => setLightbox(null)} />
    </main>
  );
}
